import { useState } from 'react'
import { fbt } from 'fbtee'
import { ChevronsUpDown, CircleDashed } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../components/ui/command'
import { UserAvatar } from '../../../components/UserAvatar'
import { InterestBadge } from '../../../components/InterestBadge'
import { useFavoriteIdSet } from '../../../contexts/FavoriteFriendsContext'
import { favoriteRingClass } from '../../../lib/favoriteRing'
import { useCoarsePointer } from '../../../hooks/useCoarsePointer'
import { friendFallbackName } from '../../../lib/commonStrings'
import { OffAppAvatar } from './TicketMarks'
import type { UserProfile } from '../../../types'

/** `me` | `open` | `name` | `user:<uid>` — who one ticket in the editor is for. */
export type HolderValue = string

/**
 * Searchable "who is this ticket for" picker: Me, Open, someone off Faro, then
 * the crew with avatars (people looking for a ticket first, then going, interested). A crew of 20+
 * stays reachable — the list scrolls under a search box instead of running
 * off the screen like a plain select.
 */
export function TicketHolderPicker({ value, label, people, profiles, selfProfile, seekerIds, goingUserIds, interestedUserIds, taken, meTaken, onPick }: {
  value: HolderValue
  /** What the closed trigger reads. */
  label: string
  people: string[]
  profiles: Record<string, UserProfile>
  selfProfile?: UserProfile
  seekerIds: ReadonlySet<string>
  goingUserIds: ReadonlySet<string>
  interestedUserIds: ReadonlySet<string>
  taken: ReadonlySet<string>
  meTaken: boolean
  onPick: (value: HolderValue) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null)
  const favoriteIdSet = useFavoriteIdSet()
  const coarse = useCoarsePointer()
  const nameOf = (uid: string) => String(profiles[uid]?.displayName?.trim() || friendFallbackName())
  // Filtered here rather than by cmdk: cmdk re-sorts matches by score and keeps
  // that order once the search is cleared, losing the crew ranking.
  const q = fold(query.trim())
  const matches = (text: string) => !q || fold(text).includes(q)
  const shownPeople = people.filter(uid => matches(nameOf(uid)))
  const meLabel = String(fbt('Me', 'Ticket picker option: the ticket is for the viewer'))
  const openLabel = String(fbt('Open', 'Ticket picker option: the ticket has no owner yet, so the crew can claim it'))
  const nameLabel = String(fbt('Someone not on Faro…', 'Ticket picker option: type the name of someone without a Faro account'))
  const setOpenAndReset = (next: boolean) => {
    setOpen(next)
    if (!next) setQuery('')
  }
  const pick = (next: HolderValue) => {
    onPick(next)
    setOpenAndReset(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpenAndReset}>
      <PopoverTrigger asChild>
        <button
          ref={setTrigger}
          type="button"
          className="flex h-8 min-w-0 flex-1 cursor-pointer items-center justify-between gap-1.5 rounded-md px-1.5 text-left text-sm hover:bg-muted/50"
          aria-label={fbt('Who is this ticket for?', 'Accessible label of the picker choosing who one ticket is for')}
          data-testid="ticket-holder-picker"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(20rem,calc(100vw-2rem))] p-0"
        align="start"
        // Portaled into the tickets dialog: its scroll lock swallows touch scrolling anywhere outside it.
        container={trigger?.closest<HTMLElement>('[data-slot="dialog-content"]')}
        // On a phone, focusing the search would pop the keyboard over a list that's usually enough to tap.
        onOpenAutoFocus={e => { if (coarse) e.preventDefault() }}
      >
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder={fbt('Search your crew…', 'Placeholder of the search box in the ticket holder picker')} />
          <CommandList>
            <CommandEmpty>{fbt('Nobody by that name', 'Empty state when no crew member matches the ticket picker search')}</CommandEmpty>
            <CommandGroup>
              {matches(meLabel) && <CommandItem value="__me" disabled={meTaken && value !== 'me'} onSelect={() => pick('me')} data-checked={value === 'me' || undefined}>
                <UserAvatar profile={selfProfile} fallbackName={selfProfile?.displayName} size="xs" />
                {meLabel}
              </CommandItem>}
              {matches(openLabel) && <CommandItem value="__open" onSelect={() => pick('open')} data-checked={value === 'open' || undefined}>
                <span className="flex size-6 shrink-0 items-center justify-center"><CircleDashed className="size-4 text-muted-foreground" /></span>
                <span className="flex flex-col">
                  {openLabel}
                  <span className="text-xs text-muted-foreground">
                    {fbt('Anyone in your crew can claim it', 'Hint under the Open option in the ticket picker')}
                  </span>
                </span>
              </CommandItem>}
              {matches(nameLabel) && <CommandItem value="__name" onSelect={() => pick('name')} data-checked={value === 'name' || undefined}>
                <OffAppAvatar />
                {nameLabel}
              </CommandItem>}
            </CommandGroup>
            {shownPeople.length > 0 && (
              <CommandGroup heading={fbt('Your crew', 'Ticket picker heading above the viewer’s friends and group members')}>
                {shownPeople.map(uid => (
                  <CommandItem
                    key={uid}
                    value={`user:${uid}`}
                    disabled={taken.has(uid)}
                    onSelect={() => pick(`user:${uid}`)}
                    data-checked={value === `user:${uid}` || undefined}
                  >
                    <UserAvatar profile={profiles[uid]} fallbackName={nameOf(uid)} size="xs" className={favoriteIdSet.has(uid) ? favoriteRingClass : undefined} />
                    <span className="min-w-0 flex-1 truncate">{nameOf(uid)}</span>
                    {seekerIds.has(uid) ? (
                      <span className="shrink-0 text-xs text-primary">
                        {fbt('Looking for a ticket', 'Hint in the ticket picker next to someone who is looking for a ticket')}
                      </span>
                    ) : (goingUserIds.has(uid) || interestedUserIds.has(uid)) && (
                      <InterestBadge status={goingUserIds.has(uid) ? 'going' : 'interested'} />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** Case- and accent-insensitive, so "jose" finds "José". */
function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}
